import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FiCopy, FiCheck } from 'react-icons/fi';

/**
 * `.df-grid` — the read-view form archetype (detail-page spec §4.A): two
 * `1fr` columns with a hairline divider, collapsing to one column under
 * 800px. Empty values render blank — never `—`/`N/A` — per the spec.
 */
function FieldGrid({ left, right }) {
	return (
		<div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-[1fr_1px_1fr] tw:gap-0'>
			<div className='tw:flex tw:flex-col tw:md:pr-8'>
				{left.map((field) => (
					<Field key={field.label} {...field} />
				))}
			</div>
			<div className='tw:hidden tw:md:block tw:bg-border' />
			<div className='tw:flex tw:flex-col tw:md:pl-8'>
				{right.map((field) => (
					<Field key={field.label} {...field} />
				))}
			</div>
		</div>
	);
}

FieldGrid.propTypes = {
	left: PropTypes.array.isRequired,
	right: PropTypes.array.isRequired,
};

/**
 * One `.df` row. `area` renders as a multi-line block with no bottom hairline
 * (observaciones); `copyable` reveals a `.df-copy` button on hover AND
 * `:focus-within` (keyboard users can't hover) — that toggle is plain CSS in
 * entity-list.css rather than Tailwind's `group` variant, which has no other
 * precedent in this codebase yet.
 */
function Field({ label, value, required, area, copyable, mono }) {
	const [copied, setCopied] = useState(false);
	const isEmpty = value === null || value === undefined || value === '';

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(String(value));
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard API unavailable (insecure context, permissions) — silently no-op.
		}
	};

	return (
		<div className='df tw:flex tw:flex-col tw:gap-[3px] tw:pt-3.5 tw:first:pt-0'>
			<span className='tw:flex tw:gap-[3px] tw:text-[12px] tw:font-semibold tw:text-accent'>
				{label}
				{required && <span className='tw:text-[13px] tw:font-bold tw:text-accent'>*</span>}
			</span>
			<div
				className={classnames(
					'tw:flex tw:min-h-[38px] tw:items-center tw:gap-2 tw:py-1.5 tw:text-[14.5px] tw:text-ink-0',
					!area && 'tw:border-b tw:border-border',
					area && 'tw:items-start tw:leading-normal tw:text-ink-1',
					mono && 'tw:font-mono',
				)}
			>
				<span className={classnames('tw:flex-1', isEmpty && 'tw:text-ink-4')}>{value}</span>
				{copyable && !isEmpty && (
					<button
						type='button'
						aria-label={copied ? 'Copiado' : `Copiar ${label.toLowerCase()}`}
						title={copied ? 'Copiado' : `Copiar ${label.toLowerCase()}`}
						onClick={handleCopy}
						className='df-copy tw:hidden tw:h-6 tw:w-6 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[5px] tw:text-ink-3 tw:hover:bg-surface-3'
					>
						{copied ? <FiCheck /> : <FiCopy />}
					</button>
				)}
			</div>
		</div>
	);
}

Field.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.node,
	required: PropTypes.bool,
	area: PropTypes.bool,
	copyable: PropTypes.bool,
	mono: PropTypes.bool,
};

export default FieldGrid;
